import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ShieldIcon from '@mui/icons-material/Shield';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LockIcon from '@mui/icons-material/Lock';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const SocServices: React.FC = () => {
  const implementationProcess = [
    {
      title: "Gap Analysis",
      description: "We assess your current controls against SOC 1 & 2 requirements to identify gaps and areas for improvement.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "Control Design",
      description: "We help design and implement appropriate controls to meet SOC 1 & 2 criteria and trust service principles.",
      icon: <SettingsSuggestIcon fontSize="large" />
    },
    {
      title: "Documentation",
      description: "We assist in creating comprehensive documentation of your control environment and processes.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Control Testing",
      description: "We perform testing of controls to ensure they are operating effectively and meeting requirements.",
      icon: <MonitorHeartIcon fontSize="large" />
    },
    {
      title: "Remediation",
      description: "We help address identified control deficiencies and strengthen your control environment.",
      icon: <BuildIcon fontSize="large" />
    },
    {
      title: "Training",
      description: "We provide training to your team on SOC requirements and maintaining effective controls.",
      icon: <SchoolIcon fontSize="large" />
    },
    {
      title: "Audit Support",
      description: "We support you through the SOC audit process, working with external auditors to ensure successful completion.",
      icon: <VerifiedUserIcon fontSize="large" />
    },
    {
      title: "Ongoing Compliance",
      description: "We help maintain ongoing compliance through continuous monitoring and periodic assessments.",
      icon: <ShieldIcon fontSize="large" />
    }
  ];

  const soc1Benefits = [
    "Demonstrates effective internal controls over financial reporting",
    "Builds trust with clients and stakeholders",
    "Meets regulatory and compliance requirements",
    "Reduces risk of financial misstatements",
    "Enhances competitive advantage in the marketplace"
  ];

  const soc2Benefits = [
    "Validates security, availability, and processing integrity of systems",
    "Demonstrates commitment to data protection and privacy",
    "Meets customer and regulatory requirements",
    "Reduces security and operational risks",
    "Builds confidence with enterprise clients"
  ];

  const trustServiceCriteria = [
    {
      principle: "Security",
      description: "Protection against unauthorized access to systems and data.",
      icon: <LockIcon fontSize="large" />
    },
    {
      principle: "Availability",
      description: "Systems are available for operation and use as committed or agreed.",
      icon: <MonitorHeartIcon fontSize="large" />
    },
    {
      principle: "Processing Integrity",
      description: "System processing is complete, valid, accurate, timely, and authorized.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      principle: "Confidentiality",
      description: "Information designated as confidential is protected as committed or agreed.",
      icon: <ShieldIcon fontSize="large" />
    },
    {
      principle: "Privacy",
      description: "Personal information is collected, used, retained, disclosed, and disposed of in conformity with commitments.",
      icon: <SecurityIcon fontSize="large" />
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              SOC 1 & 2 Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive System and Organization Controls (SOC) compliance solutions to help your organization meet audit requirements and build trust with stakeholders
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                What are SOC 1 & 2?
              </Typography>
              <Typography paragraph>
                System and Organization Controls (SOC) reports are independent third-party examinations that provide 
                assurance about the design and operating effectiveness of controls at service organizations. These reports 
                are critical for organizations that provide services to other entities.
              </Typography>
              <Typography paragraph>
                <strong>SOC 1</strong> reports focus on controls relevant to user entities' internal control over financial 
                reporting (ICFR). These reports are particularly important for service organizations that process financial 
                transactions or maintain financial records for their clients.
              </Typography>
              <Typography paragraph>
                <strong>SOC 2</strong> reports evaluate controls related to security, availability, processing integrity, 
                confidentiality, and privacy of a system. SOC 2 is especially relevant for technology and cloud service 
                providers who need to demonstrate their commitment to protecting customer data.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="SOC Compliance"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* SOC 1 vs SOC 2 Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              SOC 1 vs SOC 2: Understanding the Difference
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 4
          }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 4, 
                borderRadius: '12px',
                boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
              }}
            >
              <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
                SOC 1 (Type I & Type II)
              </Typography>
              <Typography paragraph>
                Focuses on controls at a service organization that are relevant to user entities' internal control 
                over financial reporting. Essential for organizations that process financial transactions.
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Key Benefits:
              </Typography>
              <List>
                {soc1Benefits.map((benefit, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Paper>

            <Paper 
              elevation={0}
              sx={{ 
                p: 4, 
                borderRadius: '12px',
                boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
              }}
            >
              <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
                SOC 2 (Type I & Type II)
              </Typography>
              <Typography paragraph>
                Evaluates controls related to security, availability, processing integrity, confidentiality, and 
                privacy. Critical for technology and cloud service providers.
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Key Benefits:
              </Typography>
              <List>
                {soc2Benefits.map((benefit, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Trust Service Criteria Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            SOC 2 Trust Service Criteria
          </Typography>
          <Typography 
            variant="body1" 
            textAlign="center" 
            sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
          >
            SOC 2 reports are based on five Trust Service Criteria that address different aspects of system controls.
          </Typography>
        </motion.div>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 4
        }}>
          {trustServiceCriteria.map((criterion, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  },
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ color: '#1976d2', mb: 2 }}>
                  {criterion.icon}
                </Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {criterion.principle}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {criterion.description}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Implementation Process Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Our SOC Implementation Process
            </Typography>
            <Typography 
              variant="body1" 
              textAlign="center" 
              sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
            >
              We follow a systematic approach to help your organization achieve and maintain SOC compliance.
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 4
          }}>
            {implementationProcess.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: '12px',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    },
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2
                  }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: '#1976d2', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      mr: 2
                    }}>
                      {index + 1}
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                      {step.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {step.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" color="#0c2340">
            Ready to Achieve SOC Compliance?
          </Typography>
          <Typography paragraph sx={{ maxWidth: '700px', mx: 'auto', mb: 4 }}>
            Contact our SOC compliance experts today to discuss how we can help your organization meet audit requirements 
            and build trust with your stakeholders.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            component={Link}
            to="/contact"
            endIcon={<ArrowForwardIcon />}
            sx={{ 
              py: 1.5, 
              px: 4, 
              borderRadius: '8px',
              background: 'linear-gradient(90deg, #1976d2, #2196f3)',
              '&:hover': {
                background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 15px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Get Started
          </Button>
        </motion.div>
      </Container>
    </Box>
  );
};

export default SocServices;

